import React, { useEffect, useState } from "react";
import { Image, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { StickerPlacement } from "@/src/services/api/posts/engagement-functions";
import { containRect } from "@/src/components/posts/stickers/sticker-geometry";
import PlacedSticker, { Box } from "./placed-sticker";
import { stickerAddedBy, stickersFor } from "./sticker-tooltip";

type Props = Readonly<{
  stickers: readonly StickerPlacement[] | null | undefined;
  /** Attachment index the layer covers, or -1 for the card body of a post without images. */
  mediaIndex: number;
  viewerId?: string;
  /** Explicit box (already fitted to the drawn image or body). */
  box?: Box;
  /** Or: fit the box to `imageUri` drawn with resizeMode="contain" in `area`. */
  fit?: { imageUri: string; area: { w: number; h: number } };
}>;

/**
 * Read-only stickers over a post's image or body, outside sticker mode, so
 * everyone sees them in the feed and the post view without entering the mode.
 * Only the stickers themselves take touches (for the "added by" tooltip).
 */
const StickerLayer = ({ stickers, mediaIndex, viewerId, box, fit }: Props) => {
  const { t } = useTranslation();
  const [aspect, setAspect] = useState<number | null>(null);
  const uri = fit?.imageUri;
  useEffect(() => {
    if (!uri) return;
    let live = true;
    Image.getSize(uri, (w, h) => live && setAspect(w / h || 1), () => live && setAspect(1));
    return () => { live = false; };
  }, [uri]);
  const list = stickersFor(stickers, mediaIndex);
  const area = box ?? (fit && aspect ? containRect(fit.area.w, fit.area.h, aspect) : null);
  if (!list.length || !area || area.w <= 0 || area.h <= 0) return null;
  return (
    <View
      pointerEvents="box-none"
      testID="sticker-layer"
      style={{ position: "absolute", left: area.left, top: area.top, width: area.w, height: area.h }}
    >
      {list.map((s) => (
        <PlacedSticker
          key={s.userId}
          s={s}
          box={area}
          label={stickerAddedBy(s, viewerId, t)}
          testID={`sticker-shown-${s.stickerId}`}
        />
      ))}
    </View>
  );
};

export default StickerLayer;
